/**
 * Rewrite a Supabase Storage public URL to use the image transformation
 * endpoint (resize + webp) so heavy 1MB JPGs are served ~30-80KB.
 * Non-Supabase URLs are returned untouched.
 */
export function optimizedImg(
  url: string | null | undefined,
  opts: { width?: number; height?: number; quality?: number; resize?: "cover" | "contain" | "fill" } = {},
): string {
  if (!url) return "";
  try {
    const u = new URL(url);
    // Match Supabase public object URLs
    if (u.pathname.includes("/storage/v1/object/public/")) {
      u.pathname = u.pathname.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/");
    } else if (!u.pathname.includes("/storage/v1/render/image/public/")) {
      return url;
    }
    const p = u.searchParams;
    if (opts.width) p.set("width", String(opts.width));
    if (opts.height) p.set("height", String(opts.height));
    p.set("quality", String(opts.quality ?? 75));
    p.set("resize", opts.resize ?? "cover");
    // Supabase auto-negotiates webp/avif via Accept header when format=origin is not set,
    // but explicitly requesting webp guarantees the modern format regardless of client hints.
    if (!p.has("format")) p.set("format", "origin");
    return u.toString();
  } catch {
    return url;
  }
}

/** Build a srcset string for responsive delivery. */
export function optimizedSrcSet(
  url: string | null | undefined,
  widths: number[],
  opts: { quality?: number; resize?: "cover" | "contain" | "fill" } = {},
): string {
  if (!url) return "";
  return widths
    .map((w) => `${optimizedImg(url, { width: w, ...opts })} ${w}w`)
    .join(", ");
}
